import { where, QueryConstraint } from '@firebase/firestore';
import type { Post, User } from '../types';

/**
 * Firestore rules enforce post visibility (see firestore.rules canReadPost),
 * and list queries are only granted when their filters prove the rule — an
 * unfiltered feed query is denied outright. Every post-list query therefore
 * has to carry a visibility constraint matched to who is asking. This module
 * is the single source for that viewer → constraint mapping.
 */
export type PostVisibilityScope =
  | { kind: 'public' } // signed out
  | { kind: 'authed'; uid: string; canSeeInsurance: boolean };

export function getPostVisibilityScope(
  user: Pick<User, 'id' | 'agentProfile'> | null
): PostVisibilityScope {
  if (!user) return { kind: 'public' };
  return {
    kind: 'authed',
    uid: user.id,
    // Insurance read access is gated on a server-verified agent profile, not
    // on the self-selected `roles` array (which a user can edit). This must
    // match firestore.rules isInsuranceViewer() — issuing the
    // visibility:['public','insurance'] query without a verified profile
    // would be denied by the rules and break the whole feed query.
    canSeeInsurance: user.agentProfile?.status === 'verified',
  };
}

/**
 * The 'in' shape is only legal for insurance-role viewers: rules evaluate
 * each 'in' branch independently, so the whole query — including its public
 * half — is denied for anyone who can't read insurance posts.
 */
export function visibilityConstraint(scope: PostVisibilityScope): QueryConstraint {
  return scope.kind === 'authed' && scope.canSeeInsurance
    ? where('visibility', 'in', ['public', 'insurance'])
    : where('visibility', '==', 'public');
}

/**
 * Merge the viewer's own posts (fetched via the rules-allowed authorId
 * query, so private/insurance included) into a visibility-filtered list.
 * Windowed: while more pages exist, own posts older than the oldest loaded
 * base post are dropped so they don't appear out of order ahead of pages
 * that haven't loaded yet — they surface once the window reaches them (or
 * under the feed's "Mine" filter).
 */
export function mergeOwnPosts(base: Post[], own: Post[], hasMore: boolean): Post[] {
  if (own.length === 0) return base;
  const seen = new Set(base.map((p) => p.id));
  const oldest = base.length > 0 ? base[base.length - 1].createdAt.toMillis() : 0;
  const extras = own.filter(
    (p) => !seen.has(p.id) && (!hasMore || base.length === 0 || p.createdAt.toMillis() >= oldest)
  );
  if (extras.length === 0) return base;
  return [...base, ...extras].sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
}
